import React, { useState } from 'react';
import { useDataContext } from '../../context/DataContext';
import { TableInfo } from '../../types';

const TableManagement: React.FC = () => {
  const { tables, createTable, updateTable, deleteTable } = useDataContext();
  const [isCreating, setIsCreating] = useState(false);
  const [editingTable, setEditingTable] = useState<TableInfo | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    displayOrder: 0,
    isActive: true
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      displayOrder: 0,
      isActive: true
    });
    setIsCreating(false);
    setEditingTable(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Please enter a table name');
      return;
    }

    // Check if table name already exists (for new tables or when changing name)
    const existingTable = tables.find(t => 
      t.name === formData.name.trim() && 
      (!editingTable || t.id !== editingTable.id)
    );
    
    if (existingTable) {
      alert('A table with this name already exists');
      return;
    }

    if (editingTable) {
      // Update existing table
      updateTable(editingTable.id, formData);
         } else {
       // Create new table
       createTable(formData);
     }
    
    resetForm();
  };

  const handleEdit = (table: TableInfo) => {
    setEditingTable(table);
    setFormData({
      name: table.name,
      description: table.description || '',
      displayOrder: table.displayOrder,
      isActive: table.isActive
    });
    setIsCreating(true);
  };

  const handleDelete = (table: TableInfo) => {
    if (window.confirm(`Are you sure you want to delete ${table.name}?`)) {
      deleteTable(table.id);
    }
  };

  const generateQRCode = (table: TableInfo) => {
    const shopSlug = window.location.pathname.split('/')[1];
    
    // Use the current URL structure for QR codes
    // This ensures the QR code works with whatever URL the admin is currently using
    const currentOrigin = window.location.origin;
    const tableUrl = `${currentOrigin}/${shopSlug}/table/${table.id}`;
    
    // Open QR code generator (you could replace this with a proper QR code library)
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(tableUrl)}`;
    
    const newWindow = window.open();
    if (newWindow) {
      newWindow.document.write(`
        <html>
          <head>
            <title>QR Code for ${table.name}</title>
            <style>
              body { 
                text-align: center; 
                font-family: Arial, sans-serif; 
                padding: 20px; 
                background: #f5f5f5;
                margin: 0;
              }
              .container {
                background: white;
                border-radius: 12px;
                padding: 30px;
                max-width: 400px;
                margin: 0 auto;
                box-shadow: 0 4px 16px rgba(0,0,0,0.1);
              }
              .url-display {
                background: #f8f9fa;
                border: 1px solid #dee2e6;
                border-radius: 8px;
                padding: 12px;
                margin: 20px 0;
                font-family: monospace;
                font-size: 12px;
                word-break: break-all;
                color: #495057;
              }
              .test-link {
                display: inline-block;
                background: #28a745;
                color: white;
                text-decoration: none;
                padding: 10px 20px;
                border-radius: 6px;
                font-size: 14px;
                font-weight: 600;
                margin: 10px 5px;
                transition: background-color 0.2s;
              }
              .test-link:hover {
                background: #218838;
                color: white;
                text-decoration: none;
              }
              .copy-btn {
                background: #007bff;
                color: white;
                border: none;
                padding: 8px 16px;
                border-radius: 6px;
                cursor: pointer;
                font-size: 12px;
                margin-top: 10px;
              }
              .copy-btn:hover {
                background: #0056b3;
              }
              .success {
                color: #28a745;
                font-size: 12px;
                margin-top: 5px;
              }
              .button-group {
                display: flex;
                gap: 10px;
                justify-content: center;
                flex-wrap: wrap;
                margin-top: 15px;
              }
            </style>
          </head>
          <body>
            <div class="container">
              <h2>QR Code for ${table.name}</h2>
              <p><strong>Table:</strong> ${table.name}</p>
              <img src="${qrUrl}" alt="QR Code" style="border: 1px solid #ddd; padding: 10px; border-radius: 8px;"/>
              <p style="font-size: 14px; color: #666; margin-top: 20px;">
                Scan this code to order from the table
              </p>
              <div class="url-display" id="urlDisplay">
                ${tableUrl}
              </div>
              <div class="button-group">
                <a href="${tableUrl}" target="_blank" class="test-link">
                  🧪 Test Order Page
                </a>
                <button class="copy-btn" onclick="copyUrl()">Copy URL</button>
              </div>
              <div id="copyStatus"></div>
              ${process.env.NODE_ENV === 'development' ? 
                '<p style="font-size: 11px; color: #007bff; margin-top: 10px;"><strong>Development Mode:</strong> Using current URL structure. QR code will work with your current setup.</p>' 
                : ''
              }
            </div>
            <script>
              function copyUrl() {
                const url = '${tableUrl}';
                navigator.clipboard.writeText(url).then(function() {
                  document.getElementById('copyStatus').innerHTML = '<div class="success">URL copied to clipboard!</div>';
                  setTimeout(() => {
                    document.getElementById('copyStatus').innerHTML = '';
                  }, 2000);
                }).catch(function(err) {
                  console.error('Failed to copy: ', err);
                  document.getElementById('copyStatus').innerHTML = '<div style="color: #dc3545; font-size: 12px;">Failed to copy URL</div>';
                });
              }
            </script>
          </body>
        </html>
      `);
    }
  };

  return (
    <div className="fade-in">
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <h2 className="card-title">Table Management</h2>
            <button
              className="btn btn-primary"
              onClick={() => setIsCreating(true)}
            >
              + Add Table
            </button>
          </div>
        </div>

        {/* Create/Edit Form */}
        {isCreating && (
          <div style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '1.5rem', 
            borderRadius: '8px', 
            marginBottom: '2rem',
            border: '1px solid #dee2e6'
          }}>
            <h3 style={{ marginBottom: '1rem' }}>
              {editingTable ? 'Edit Table' : 'Add New Table'}
            </h3>
            <form onSubmit={handleSubmit}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                <div className="form-group">
                  <label className="form-label">Table Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Table 1, Window Table"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Internal description for staff (optional)"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Display Order</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={formData.isActive ? 'active' : 'inactive'}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="submit" className="btn btn-primary">
                  {editingTable ? 'Update Table' : 'Add Table'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={resetForm}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Tables Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '1.5rem'
        }}>
          {tables.length === 0 ? (
            <div style={{ 
              gridColumn: '1 / -1',
              textAlign: 'center', 
              padding: '3rem',
              color: '#666'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🪑</div>
              <h3>No tables yet</h3>
              <p>Add your first table to get started</p>
            </div>
          ) : (
            tables.map(table => (
              <div
                key={table.id}
                className="card"
                style={{
                  border: table.isActive ? '2px solid #27ae60' : '2px solid #e74c3c',
                  opacity: table.isActive ? 1 : 0.7
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50' }}>
                      {table.name}
                    </h3>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>
                      Order: {table.displayOrder}
                    </div>
                    {table.description && (
                      <div style={{ fontSize: '0.8rem', color: '#888', fontStyle: 'italic' }}>
                        {table.description}
                      </div>
                    )}
                  </div>
                  <div style={{
                    backgroundColor: table.isActive ? '#27ae60' : '#e74c3c',
                    color: 'white',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: '600'
                  }}>
                    {table.isActive ? 'Active' : 'Inactive'}
                  </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                    Table ID: {table.id}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => generateQRCode(table)}
                    style={{ fontSize: '0.8rem', flex: '1', minWidth: '120px' }}
                  >
                    📱 Generate QR
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleEdit(table)}
                    style={{ fontSize: '0.8rem' }}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDelete(table)}
                    style={{ fontSize: '0.8rem' }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Instructions */}
        <div style={{ 
          marginTop: '2rem', 
          padding: '1.5rem', 
          backgroundColor: '#e3f2fd', 
          borderRadius: '8px',
          border: '1px solid #bbdefb'
        }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#1976d2' }}>
            📋 How to use Table Management
          </h4>
          <ul style={{ margin: '0', paddingLeft: '1.5rem', color: '#1565c0' }}>
            <li>Create tables for your restaurant or cafe</li>
            <li>Generate QR codes for each table that customers can scan</li>
            <li>Customers scan the QR code to access the ordering system for that specific table</li>
            <li>Orders placed via QR codes will automatically include the table number</li>
            <li>Toggle tables active/inactive to temporarily disable ordering from specific tables</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TableManagement; 